/**
 * 서비스 접근 섹션 컴포넌트
 * ServiceDetailDialog에서 분리
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { MSAService } from '@/types';

interface SubscriptionInfo {
  is_trial?: boolean;
  plan_name?: string;
  trial_remaining?: number;
  trial_remaining_uses?: number;
}

interface UserInfo {
  name?: string;
  email: string;
}

interface ServiceAccessSectionProps {
  service: MSAService;
  user: UserInfo | null;
  isSubscribed: boolean;
  currentSubscription: SubscriptionInfo | null;
  isDeleted: boolean;
  isOnline: boolean;
  onConnect: (service: MSAService) => Promise<void>;
}

export const ServiceAccessSection: React.FC<ServiceAccessSectionProps> = ({
  service,
  user,
  isSubscribed,
  currentSubscription,
  isDeleted,
  isOnline,
  onConnect,
}) => {
  const [connectError, setConnectError] = useState<string>('');

  const handleConnect = async () => {
    setConnectError('');
    try {
      await onConnect(service);
    } catch (error: any) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || error?.message;

      if (status === 403) {
        if (detail?.includes('활성 구독')) {
          setConnectError('이 서비스에 대한 활성 구독이 없습니다. 서비스 이용에서 구독해 주세요.');
        } else if (detail?.includes('연결되지 않은')) {
          setConnectError('이 서비스는 아직 연결되지 않았습니다.');
        } else {
          setConnectError(detail || '서비스 접근 권한이 없습니다.');
        }
      } else if (status === 402) {
        if (detail?.includes('체험판 이용이 만료')) {
          setConnectError(detail);
        } else {
          setConnectError('크레딧이 부족합니다. 관리자에게 문의해 주세요.');
        }
      } else {
        setConnectError(detail || '서비스로 이동하는데 실패했습니다.');
      }
    }
  };

  const renderCurrentPlan = () => {
    if (currentSubscription?.is_trial &&
        (currentSubscription?.trial_remaining !== undefined ||
         currentSubscription?.trial_remaining_uses !== undefined)) {
      return (
        <Badge variant="warning" size="sm" className="font-semibold">
          체험판 ({(currentSubscription.trial_remaining ?? currentSubscription.trial_remaining_uses ?? 0)}회 잔여)
        </Badge>
      );
    }

    if (currentSubscription?.plan_name) {
      return (
        <Badge variant="success" size="sm" className="font-semibold">
          {currentSubscription.plan_name}
        </Badge>
      );
    }

    return (
      <Badge variant="warning" size="sm" className="font-semibold">
        체험판
      </Badge>
    );
  };

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-2">서비스 접근</h3>

      {isSubscribed && user && (
        <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <div>
                <span className="text-gray-600">사용자:</span>
                <span className="ml-2 font-medium text-gray-900">{user.name || user.email}</span>
              </div>
              <div>
                <span className="text-gray-600">이메일:</span>
                <span className="ml-2 font-medium text-gray-900">{user.email}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">과금제:</span>
              {renderCurrentPlan()}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="text-sm text-gray-600 break-all bg-gray-50 p-3 rounded-md flex-1">
          {service.service_url || 'URL 정보 없음'}
        </div>
        {isSubscribed && (
          <Button
            size="sm"
            onClick={handleConnect}
            disabled={isDeleted || !isOnline}
          >
            서비스 접속
          </Button>
        )}
      </div>

      {connectError && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{connectError}</p>
        </div>
      )}

      {!isSubscribed && (
        <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            이 서비스는 아직 구독되지 않았습니다. 구독하기 버튼을 눌러 구독해주세요.
          </p>
        </div>
      )}
    </div>
  );
};

export default ServiceAccessSection;
